<template>
  <div class="page-content dashboard-page">
    <!-- <breadcrumb-index :items="breadcrumbs" /> -->

    <!-- Welcome Banner -->
    <div class="welcome-banner">
      <div class="welcome-content">
        <h4 class="welcome-title">Welcome back, {{ user?.fullName || 'Admin' }}</h4>
        <p class="welcome-subtitle">
          <span class="role-badge" :class="'role-' + userRole">{{ formatRole(userRole) }}</span>
          <span class="welcome-date">{{ currentDate }}</span>
        </p>
      </div>
      <div class="welcome-actions" v-if="isSuperAdmin || isAdmin">
        <router-link to="/app/exams/list" class="action-btn action-primary">
          <vue-feather type="plus" size="16" /> New Exam
        </router-link>
        <router-link to="/app/questions/list" class="action-btn action-secondary">
          <vue-feather type="help-circle" size="16" /> Questions
        </router-link>
      </div>
    </div>

    <!-- Super Admin: System Health Bar -->
    <div v-if="isSuperAdmin" class="system-health-bar">
      <div class="health-item" :class="'health-' + systemHealth.apiStatus">
        <vue-feather type="server" size="14" />
        <span>API: {{ systemHealth.apiStatus }}</span>
      </div>
      <div class="health-item" :class="'health-' + systemHealth.dbStatus">
        <vue-feather type="database" size="14" />
        <span>Database: {{ systemHealth.dbStatus }}</span>
      </div>
      <div class="health-item">
        <vue-feather type="hard-drive" size="14" />
        <span>Storage: {{ systemHealth.storageUsed }} / {{ systemHealth.storageTotal }}</span>
      </div>
      <div class="health-item">
        <vue-feather type="clock" size="14" />
        <span>Uptime: {{ systemHealth.uptime }}</span>
      </div>
    </div>

    <!-- Primary Stats Row (All Roles) -->
    <div class="stats-grid" :class="isSuperAdmin ? 'stats-6' : 'stats-4'">
      <div class="stat-card stat-primary" @click="$router.push('/app/exams/list')">
        <div class="stat-icon"><vue-feather type="file-text" size="22" /></div>
        <div class="stat-text">
          <span class="stat-value">{{ animatedStats.totalExams }}</span>
          <span class="stat-label">Total Exams</span>
        </div>
        <div class="stat-trend" v-if="stats.examsTrend" :class="stats.examsTrend > 0 ? 'trend-up' : 'trend-down'">
          <vue-feather :type="stats.examsTrend > 0 ? 'trending-up' : 'trending-down'" size="14" />
          {{ Math.abs(stats.examsTrend) }}%
        </div>
      </div>

      <div class="stat-card stat-success" @click="$router.push('/app/questions/list')">
        <div class="stat-icon"><vue-feather type="help-circle" size="22" /></div>
        <div class="stat-text">
          <span class="stat-value">{{ animatedStats.totalQuestions }}</span>
          <span class="stat-label">Questions</span>
        </div>
        <div class="stat-trend" v-if="stats.questionsTrend" :class="stats.questionsTrend > 0 ? 'trend-up' : 'trend-down'">
          <vue-feather :type="stats.questionsTrend > 0 ? 'trending-up' : 'trending-down'" size="14" />
          {{ Math.abs(stats.questionsTrend) }}%
        </div>
      </div>

      <div class="stat-card stat-warning" @click="$router.push('/app/notes/list')">
        <div class="stat-icon"><vue-feather type="book-open" size="22" /></div>
        <div class="stat-text">
          <span class="stat-value">{{ animatedStats.totalNotes }}</span>
          <span class="stat-label">Notes</span>
        </div>
      </div>

      <div class="stat-card stat-cyan" @click="$router.push('/app/approvals')">
        <div class="stat-icon"><vue-feather type="check-square" size="22" /></div>
        <div class="stat-text">
          <span class="stat-value">{{ animatedStats.pendingApprovals }}</span>
          <span class="stat-label">Pending Approvals</span>
        </div>
      </div>

      <!-- Super Admin Only -->
      <div v-if="isSuperAdmin" class="stat-card stat-purple" @click="$router.push('/app/users/app-users')">
        <div class="stat-icon"><vue-feather type="users" size="22" /></div>
        <div class="stat-text">
          <span class="stat-value">{{ animatedStats.totalAppUsers }}</span>
          <span class="stat-label">App Users</span>
        </div>
        <div class="stat-trend" v-if="stats.usersTrend" :class="stats.usersTrend > 0 ? 'trend-up' : 'trend-down'">
          <vue-feather :type="stats.usersTrend > 0 ? 'trending-up' : 'trending-down'" size="14" />
          {{ Math.abs(stats.usersTrend) }}%
        </div>
      </div>

      <div v-if="isSuperAdmin" class="stat-card stat-rose" @click="$router.push('/app/reports')">
        <div class="stat-icon"><vue-feather type="activity" size="22" /></div>
        <div class="stat-text">
          <span class="stat-value">{{ animatedStats.totalTestSessions }}</span>
          <span class="stat-label">Test Sessions</span>
        </div>
      </div>
    </div>

    <!-- Super Admin: User Analytics Row -->
    <div v-if="isSuperAdmin" class="row g-3 mb-3">
      <div class="col-lg-8">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h6 class="card-title mb-0">User Growth (Last 30 Days)</h6>
            <div class="chart-period-btns">
              <button v-for="p in ['7d','30d','90d']" :key="p"
                :class="['period-btn', { active: userGrowthPeriod === p }]"
                @click="changeUserGrowthPeriod(p)">{{ p }}</button>
            </div>
          </div>
          <div class="card-body">
            <div class="chart-container">
              <div class="mini-chart-bars">
                <div v-for="(bar, i) in userGrowthData" :key="i" class="chart-bar-wrapper">
                  <div class="chart-bar" :style="{ height: bar.height + '%' }" :title="bar.label + ': ' + bar.value">
                    <span class="bar-tooltip">{{ bar.value }}</span>
                  </div>
                  <span class="bar-label">{{ bar.label }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-4">
        <div class="card h-100">
          <div class="card-header">
            <h6 class="card-title mb-0">User Breakdown</h6>
          </div>
          <div class="card-body">
            <div class="breakdown-list">
              <div class="breakdown-item">
                <div class="breakdown-label">
                  <span class="breakdown-dot dot-green"></span> Active Users
                </div>
                <div class="breakdown-value">{{ userBreakdown.active }}</div>
                <div class="breakdown-bar"><div class="bar-fill bar-green" :style="{ width: userBreakdown.activePercent + '%' }"></div></div>
              </div>
              <div class="breakdown-item">
                <div class="breakdown-label">
                  <span class="breakdown-dot dot-yellow"></span> Inactive Users
                </div>
                <div class="breakdown-value">{{ userBreakdown.inactive }}</div>
                <div class="breakdown-bar"><div class="bar-fill bar-yellow" :style="{ width: userBreakdown.inactivePercent + '%' }"></div></div>
              </div>
              <div class="breakdown-item">
                <div class="breakdown-label">
                  <span class="breakdown-dot dot-blue"></span> Premium Users
                </div>
                <div class="breakdown-value">{{ userBreakdown.premium }}</div>
                <div class="breakdown-bar"><div class="bar-fill bar-blue" :style="{ width: userBreakdown.premiumPercent + '%' }"></div></div>
              </div>
              <div class="breakdown-item">
                <div class="breakdown-label">
                  <span class="breakdown-dot dot-gray"></span> Free Users
                </div>
                <div class="breakdown-value">{{ userBreakdown.free }}</div>
                <div class="breakdown-bar"><div class="bar-fill bar-gray" :style="{ width: userBreakdown.freePercent + '%' }"></div></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Content Overview Row (Admin + Super Admin) -->
    <div v-if="isSuperAdmin || isAdmin" class="row g-3 mb-3">
      <div class="col-lg-8">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h6 class="card-title mb-0">Content Overview</h6>
            <router-link to="/app/exams/list" class="btn-link-sm">View All →</router-link>
          </div>
          <div class="card-body">
            <div class="content-grid">
              <div class="content-stat" v-for="item in contentOverview" :key="item.label">
                <vue-feather :type="item.icon" size="18" :class="'text-' + item.color" />
                <div class="content-stat-info">
                  <span class="content-stat-value">{{ item.value }}</span>
                  <span class="content-stat-label">{{ item.label }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-4">
        <div class="card h-100">
          <div class="card-header">
            <h6 class="card-title mb-0">Content Status</h6>
          </div>
          <div class="card-body">
            <div class="status-list">
              <div class="status-item" v-for="s in contentStatus" :key="s.label">
                <span class="status-dot" :class="'dot-' + s.color"></span>
                <span class="status-label">{{ s.label }}</span>
                <span class="status-count">{{ s.count }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content Row -->
    <div class="row g-3 mb-3">
      <!-- Recent Activity / Exams -->
      <div class="col-lg-8">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h6 class="card-title mb-0">{{ isReviewer ? 'Items Pending Review' : 'Recent Exams' }}</h6>
            <router-link :to="isReviewer ? '/app/approvals' : '/app/exams/list'" class="btn-link-sm">
              View All →
            </router-link>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead>
                  <tr v-if="isReviewer">
                    <th>Item</th>
                    <th>Type</th>
                    <th>Submitted By</th>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                  <tr v-else>
                    <th>Exam Name</th>
                    <th>Status</th>
                    <th>Subjects</th>
                    <th>Questions</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-if="isReviewer">
                    <tr v-for="item in pendingReviews" :key="item.id">
                      <td class="fw-medium">{{ item.title }}</td>
                      <td><span class="type-badge" :class="'type-' + item.type">{{ item.type }}</span></td>
                      <td>{{ item.submittedBy }}</td>
                      <td>{{ formatDate(item.createdAt) }}</td>
                      <td>
                        <router-link :to="'/app/approvals'" class="btn btn-sm btn-outline-primary">Review</router-link>
                      </td>
                    </tr>
                  </template>
                  <template v-else>
                    <tr v-for="exam in recentExams" :key="exam.id">
                      <td class="fw-medium">{{ exam.name }}</td>
                      <td><span :class="['status-badge', 'status-' + exam.status]">{{ exam.status }}</span></td>
                      <td>{{ exam.subjectCount || 0 }}</td>
                      <td>{{ exam.questionCount || 0 }}</td>
                      <td>{{ formatDate(exam.createdAt) }}</td>
                    </tr>
                  </template>
                  <tr v-if="(isReviewer ? pendingReviews : recentExams).length === 0">
                    <td :colspan="5" class="text-center text-muted py-4">No items found</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Sidebar Cards -->
      <div class="col-lg-4">
        <!-- Quick Actions -->
        <div class="card mb-2">
          <div class="card-header">
            <h6 class="card-title mb-0">Quick Actions</h6>
          </div>
          <div class="card-body">
            <div class="quick-actions-grid">
              <router-link v-if="isSuperAdmin || isAdmin" to="/app/exams/list" class="quick-action-item">
                <vue-feather type="plus-circle" size="20" />
                <span>New Exam</span>
              </router-link>
              <router-link to="/app/questions/list" class="quick-action-item">
                <vue-feather type="help-circle" size="20" />
                <span>Questions</span>
              </router-link>
              <router-link to="/app/notes/list" class="quick-action-item">
                <vue-feather type="book-open" size="20" />
                <span>Notes</span>
              </router-link>
              <router-link to="/app/approvals" class="quick-action-item">
                <vue-feather type="check-square" size="20" />
                <span>Approvals</span>
              </router-link>
              <router-link v-if="isSuperAdmin || isAdmin" to="/app/flashcards/list" class="quick-action-item">
                <vue-feather type="layers" size="20" />
                <span>Flashcards</span>
              </router-link>
              <router-link v-if="isSuperAdmin" to="/app/users/list" class="quick-action-item">
                <vue-feather type="users" size="20" />
                <span>Users</span>
              </router-link>
              <router-link to="/app/bulletins/list" class="quick-action-item">
                <vue-feather type="radio" size="20" />
                <span>Bulletins</span>
              </router-link>
              <router-link to="/app/reports" class="quick-action-item">
                <vue-feather type="bar-chart-2" size="20" />
                <span>Reports</span>
              </router-link>
            </div>
          </div>
        </div>

        <!-- Recent Notifications -->
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h6 class="card-title mb-0">Recent Notifications</h6>
            <router-link to="/app/notifications" class="btn-link-sm">All →</router-link>
          </div>
          <div class="card-body p-0">
            <div class="notification-list">
              <div v-for="n in recentNotifications" :key="n.id" class="notification-item">
                <div class="notification-icon" :class="'notif-' + n.type">
                  <vue-feather :type="getNotifIcon(n.type)" size="14" />
                </div>
                <div class="notification-content">
                  <span class="notification-text">{{ n.title }}</span>
                  <span class="notification-time">{{ timeAgo(n.sentAt) }}</span>
                </div>
              </div>
              <div v-if="!recentNotifications.length" class="text-center text-muted py-3 small">
                No recent notifications
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Super Admin: Admin Activity & Test Performance -->
    <div v-if="isSuperAdmin" class="row g-3 mb-3">
      <div class="col-lg-6">
        <div class="card">
          <div class="card-header">
            <h6 class="card-title mb-0">Admin Activity Log</h6>
          </div>
          <div class="card-body p-0">
            <div class="activity-list">
              <div v-for="a in adminActivity" :key="a.id" class="activity-item">
                <div class="activity-avatar">{{ getInitial(a.adminName) }}</div>
                <div class="activity-content">
                  <span class="activity-text">
                    <strong>{{ a.adminName }}</strong> {{ a.action }}
                  </span>
                  <span class="activity-time">{{ timeAgo(a.timestamp) }}</span>
                </div>
              </div>
              <div v-if="!adminActivity.length" class="text-center text-muted py-3 small">
                No recent activity
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-6">
        <div class="card">
          <div class="card-header">
            <h6 class="card-title mb-0">Test Performance Summary</h6>
          </div>
          <div class="card-body">
            <div class="performance-grid">
              <div class="perf-item">
                <span class="perf-value">{{ testPerformance.avgScore }}%</span>
                <span class="perf-label">Avg Score</span>
                <div class="perf-bar"><div class="perf-fill" :style="{ width: testPerformance.avgScore + '%' }"></div></div>
              </div>
              <div class="perf-item">
                <span class="perf-value">{{ testPerformance.passRate }}%</span>
                <span class="perf-label">Pass Rate</span>
                <div class="perf-bar"><div class="perf-fill perf-green" :style="{ width: testPerformance.passRate + '%' }"></div></div>
              </div>
              <div class="perf-item">
                <span class="perf-value">{{ testPerformance.completionRate }}%</span>
                <span class="perf-label">Completion Rate</span>
                <div class="perf-bar"><div class="perf-fill perf-blue" :style="{ width: testPerformance.completionRate + '%' }"></div></div>
              </div>
              <div class="perf-item">
                <span class="perf-value">{{ testPerformance.avgTimeMins }}m</span>
                <span class="perf-label">Avg Time</span>
              </div>
            </div>
            <div class="top-tests mt-3">
              <h6 class="small fw-semibold text-muted mb-2">Top Performing Tests</h6>
              <div v-for="t in testPerformance.topTests" :key="t.id" class="top-test-item">
                <span class="top-test-name">{{ t.name }}</span>
                <span class="top-test-score">{{ t.avgScore }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- System Info (All Roles) -->
    <div class="row g-3">
      <div class="col-lg-4">
        <div class="card">
          <div class="card-header">
            <h6 class="card-title mb-0">Your Profile</h6>
          </div>
          <div class="card-body py-2">
            <ul class="info-list">
              <li>
                <span class="info-label">Name</span>
                <span class="info-value">{{ user?.fullName || '-' }}</span>
              </li>
              <li>
                <span class="info-label">Email</span>
                <span class="info-value">{{ user?.email || '-' }}</span>
              </li>
              <li>
                <span class="info-label">Role</span>
                <span class="role-badge" :class="'role-' + userRole">{{ formatRole(userRole) }}</span>
              </li>
              <li>
                <span class="info-label">2FA</span>
                <span :class="user?.is2faEnabled ? 'text-success' : 'text-danger'">
                  {{ user?.is2faEnabled ? 'Enabled' : 'Disabled' }}
                </span>
              </li>
              <li>
                <span class="info-label">Last Login</span>
                <span class="info-value">{{ formatDate(user?.lastLogin) }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Super Admin: Admin Users Overview -->
      <div v-if="isSuperAdmin" class="col-lg-4">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h6 class="card-title mb-0">Admin Users</h6>
            <router-link to="/app/users/list" class="btn-link-sm">Manage →</router-link>
          </div>
          <div class="card-body p-0">
            <div class="admin-users-list">
              <div v-for="admin in adminUsers" :key="admin.id" class="admin-user-item">
                <div class="admin-avatar">{{ getInitial(admin.fullName) }}</div>
                <div class="admin-info">
                  <span class="admin-name">{{ admin.fullName }}</span>
                  <span class="admin-role">{{ formatRole(admin.role) }}</span>
                </div>
                <span class="admin-status" :class="admin.isActive ? 'active' : 'inactive'">
                  {{ admin.isActive ? 'Active' : 'Inactive' }}
                </span>
              </div>
              <div v-if="!adminUsers.length" class="text-center text-muted py-3 small">
                No admin users
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Super Admin: Storage & Resources -->
      <div v-if="isSuperAdmin" class="col-lg-4">
        <div class="card">
          <div class="card-header">
            <h6 class="card-title mb-0">Resources & Storage</h6>
          </div>
          <div class="card-body">
            <div class="resource-item mb-3">
              <div class="d-flex justify-content-between mb-1">
                <span class="small text-muted">File Storage</span>
                <span class="small fw-medium">{{ storageInfo.filesUsed }} / {{ storageInfo.filesTotal }}</span>
              </div>
              <div class="resource-bar">
                <div class="resource-fill" :class="storageInfo.filesPercent > 80 ? 'fill-danger' : 'fill-primary'"
                  :style="{ width: storageInfo.filesPercent + '%' }"></div>
              </div>
            </div>
            <div class="resource-item mb-3">
              <div class="d-flex justify-content-between mb-1">
                <span class="small text-muted">Database</span>
                <span class="small fw-medium">{{ storageInfo.dbUsed }} / {{ storageInfo.dbTotal }}</span>
              </div>
              <div class="resource-bar">
                <div class="resource-fill" :class="storageInfo.dbPercent > 80 ? 'fill-danger' : 'fill-primary'"
                  :style="{ width: storageInfo.dbPercent + '%' }"></div>
              </div>
            </div>
            <div class="resource-item">
              <div class="d-flex justify-content-between mb-1">
                <span class="small text-muted">API Calls (Today)</span>
                <span class="small fw-medium">{{ storageInfo.apiCalls }}</span>
              </div>
              <div class="resource-bar">
                <div class="resource-fill fill-blue" :style="{ width: storageInfo.apiCallsPercent + '%' }"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Reviewer: My Review Stats -->
      <div v-if="isReviewer" class="col-lg-4">
        <div class="card">
          <div class="card-header">
            <h6 class="card-title mb-0">My Review Stats</h6>
          </div>
          <div class="card-body">
            <div class="review-stats">
              <div class="review-stat-item">
                <span class="review-stat-value text-success">{{ reviewerStats.approved }}</span>
                <span class="review-stat-label">Approved</span>
              </div>
              <div class="review-stat-item">
                <span class="review-stat-value text-danger">{{ reviewerStats.rejected }}</span>
                <span class="review-stat-label">Rejected</span>
              </div>
              <div class="review-stat-item">
                <span class="review-stat-value text-warning">{{ reviewerStats.pending }}</span>
                <span class="review-stat-label">Pending</span>
              </div>
              <div class="review-stat-item">
                <span class="review-stat-value text-info">{{ reviewerStats.total }}</span>
                <span class="review-stat-label">Total</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Admin: Approval Queue Summary -->
      <div v-if="isAdmin && !isSuperAdmin" class="col-lg-4">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h6 class="card-title mb-0">Approval Queue</h6>
            <router-link to="/app/approvals" class="btn-link-sm">View →</router-link>
          </div>
          <div class="card-body">
            <div class="approval-summary">
              <div class="approval-stat">
                <vue-feather type="clock" size="16" class="text-warning" />
                <span>{{ approvalQueue.pending }} Pending</span>
              </div>
              <div class="approval-stat">
                <vue-feather type="check" size="16" class="text-success" />
                <span>{{ approvalQueue.approvedToday }} Approved Today</span>
              </div>
              <div class="approval-stat">
                <vue-feather type="x" size="16" class="text-danger" />
                <span>{{ approvalQueue.rejectedToday }} Rejected Today</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading Overlay -->
    <div v-if="loading" class="dashboard-loading">
      <div class="spinner"></div>
      <span>Loading dashboard...</span>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import api from '@/services/api'

export default {
  name: 'DashboardPage',
  setup() {
    const authStore = useAuthStore()
    const user = computed(() => authStore.user)
    const userRole = computed(() => authStore.userRole)
    const isSuperAdmin = computed(() => authStore.isSuperAdmin)
    const isAdmin = computed(() => authStore.isAdmin)
    const isReviewer = computed(() => authStore.isReviewer)

    const breadcrumbs = [{ title: 'Dashboard', active: true }]
    const loading = ref(true)
    const userGrowthPeriod = ref('30d')

    const currentDate = computed(() => {
      return new Date().toLocaleDateString('en-US', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
      })
    })

    // Stats
    const stats = ref({
      totalExams: 0, totalQuestions: 0, totalNotes: 0, pendingApprovals: 0,
      totalAppUsers: 0, totalTestSessions: 0,
      examsTrend: 0, questionsTrend: 0, usersTrend: 0
    })

    const animatedStats = ref({ ...stats.value })

    // System Health (Super Admin)
    const systemHealth = ref({
      apiStatus: 'healthy', dbStatus: 'healthy',
      storageUsed: '0 GB', storageTotal: '0 GB', uptime: '0d'
    })

    // User Growth (Super Admin)
    const userGrowthData = ref([])
    const userBreakdown = ref({
      active: 0, inactive: 0, premium: 0, free: 0,
      activePercent: 0, inactivePercent: 0, premiumPercent: 0, freePercent: 0
    })

    // Content Overview (Admin + Super Admin)
    const contentOverview = ref([])
    const contentStatus = ref([])

    // Recent data
    const recentExams = ref([])
    const pendingReviews = ref([])
    const recentNotifications = ref([])

    // Super Admin specific
    const adminActivity = ref([])
    const adminUsers = ref([])
    const testPerformance = ref({
      avgScore: 0, passRate: 0, completionRate: 0, avgTimeMins: 0, topTests: []
    })
    const storageInfo = ref({
      filesUsed: '0 GB', filesTotal: '10 GB', filesPercent: 0,
      dbUsed: '0 GB', dbTotal: '5 GB', dbPercent: 0,
      apiCalls: 0, apiCallsPercent: 0
    })

    // Reviewer specific
    const reviewerStats = ref({ approved: 0, rejected: 0, pending: 0, total: 0 })

    // Admin specific
    const approvalQueue = ref({ pending: 0, approvedToday: 0, rejectedToday: 0 })

    // Animate counter
    function animateValue(key, target) {
      const duration = 800
      const start = animatedStats.value[key] || 0
      const diff = target - start
      if (diff === 0) { animatedStats.value[key] = target; return }
      const startTime = performance.now()
      function step(now) {
        const elapsed = now - startTime
        const progress = Math.min(elapsed / duration, 1)
        const eased = 1 - Math.pow(1 - progress, 3)
        animatedStats.value[key] = Math.round(start + diff * eased)
        if (progress < 1) requestAnimationFrame(step)
      }
      requestAnimationFrame(step)
    }

    // Fetch dashboard data based on role
    async function fetchDashboardData() {
      loading.value = true
      try {
        const response = await api.get('/v1/admin/dashboard/stats')
        const data = response.data

        // Common stats
        stats.value = { ...stats.value, ...data.stats }
        Object.keys(data.stats || {}).forEach(key => {
          if (key in animatedStats.value) animateValue(key, data.stats[key])
        })

        // Recent exams
        if (data.recentExams) recentExams.value = data.recentExams

        // Notifications
        if (data.recentNotifications) recentNotifications.value = data.recentNotifications

        // Content overview
        if (data.contentOverview) contentOverview.value = data.contentOverview
        if (data.contentStatus) contentStatus.value = data.contentStatus

        // Approval queue
        if (data.approvalQueue) approvalQueue.value = data.approvalQueue

        // Super Admin specific
        if (isSuperAdmin.value) {
          if (data.systemHealth) systemHealth.value = data.systemHealth
          if (data.userGrowth) userGrowthData.value = normalizeChartData(data.userGrowth)
          if (data.userBreakdown) userBreakdown.value = data.userBreakdown
          if (data.adminActivity) adminActivity.value = data.adminActivity
          if (data.adminUsers) adminUsers.value = data.adminUsers
          if (data.testPerformance) testPerformance.value = data.testPerformance
          if (data.storageInfo) storageInfo.value = data.storageInfo
        }

        // Reviewer specific
        if (isReviewer.value) {
          if (data.reviewerStats) reviewerStats.value = data.reviewerStats
          if (data.pendingReviews) pendingReviews.value = data.pendingReviews
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error)
      } finally {
        loading.value = false
      }
    }

    function normalizeChartData(data) {
      if (!data || !data.length) return []
      const max = Math.max(...data.map(d => d.value), 1)
      return data.map(d => ({ ...d, height: (d.value / max) * 100 }))
    }

    function changeUserGrowthPeriod(period) {
      userGrowthPeriod.value = period
      // Re-fetch with period
      api.get(`/v1/admin/dashboard/user-growth?period=${period}`)
        .then(res => { userGrowthData.value = normalizeChartData(res.data.userGrowth) })
        .catch(err => console.error('Failed to fetch user growth:', err))
    }

    // Helpers
    function formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
      })
    }

    function formatRole(role) {
      if (!role) return ''
      return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }

    function timeAgo(date) {
      if (!date) return ''
      const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000)
      if (seconds < 60) return 'Just now'
      if (seconds < 3600) return Math.floor(seconds / 60) + 'm ago'
      if (seconds < 86400) return Math.floor(seconds / 3600) + 'h ago'
      return Math.floor(seconds / 86400) + 'd ago'
    }

    function getInitial(name) {
      return (name || 'A').charAt(0).toUpperCase()
    }

    function getNotifIcon(type) {
      const icons = {
        info: 'info', success: 'check-circle', warning: 'alert-triangle',
        error: 'alert-circle', exam: 'file-text', user: 'user-plus',
        approval: 'check-square'
      }
      return icons[type] || 'bell'
    }

    onMounted(() => { fetchDashboardData() })

    return {
      user, userRole, isSuperAdmin, isAdmin, isReviewer,
      breadcrumbs, loading, currentDate,
      stats, animatedStats,
      systemHealth, userGrowthPeriod, userGrowthData, userBreakdown,
      contentOverview, contentStatus,
      recentExams, pendingReviews, recentNotifications,
      adminActivity, adminUsers, testPerformance, storageInfo,
      reviewerStats, approvalQueue,
      formatDate, formatRole, timeAgo, getInitial, getNotifIcon,
      changeUserGrowthPeriod
    }
  }
}
</script>

<style scoped>
/* Welcome Banner */
.dashboard-page {
  max-width: 100%;
}
.dashboard-page .card {
  margin-bottom: 0;
}
.welcome-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, #003049 0%, #00506e 100%);
  border-radius: 14px;
  margin-bottom: 14px;
  color: white;
}
.welcome-title { font-size: 20px; font-weight: 600; margin: 0 0 6px; color: #ffffff; }
.welcome-subtitle { display: flex; align-items: center; gap: 12px; margin: 0; font-size: 13px; color: rgba(255, 255, 255, 0.8); }
.welcome-date { font-size: 13px; color: rgba(255, 255, 255, 0.8); }
.welcome-actions { display: flex; gap: 8px; }
.action-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 500;
  text-decoration: none; transition: all 0.2s;
}
.action-primary { background: #fcbf49; color: #003049; }
.action-primary:hover { background: #f5b23e; transform: translateY(-1px); }
.action-secondary { background: rgba(255,255,255,0.15); color: white; }
.action-secondary:hover { background: rgba(255,255,255,0.25); }

/* Role Badge */
.role-badge {
  display: inline-block; padding: 2px 10px; border-radius: 20px;
  font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;
}
.role-super_admin { background: rgba(239,68,68,0.15); color: #ef4444; }
.role-admin { background: rgba(59,130,246,0.15); color: #3b82f6; }
.role-reviewer { background: rgba(168,85,247,0.15); color: #a855f7; }
.role-content_manager { background: rgba(34,197,94,0.15); color: #22c55e; }

/* System Health Bar */
.system-health-bar {
  display: flex; gap: 16px; padding: 8px 14px;
  background: white; border-radius: 10px; margin-bottom: 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06); flex-wrap: wrap;
}
.health-item {
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 500; color: #6b7280;
}
.health-healthy { color: #22c55e; }
.health-degraded { color: #f59e0b; }
.health-down { color: #ef4444; }

/* Stats Grid */
.stats-grid {
  display: grid; gap: 10px; margin-bottom: 14px;
}
.stats-4 { grid-template-columns: repeat(4, 1fr); }
.stats-6 { grid-template-columns: repeat(6, 1fr); }
.stat-card {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 14px; background: white; border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  cursor: pointer; transition: transform 0.2s, box-shadow 0.2s;
  position: relative;
}
.stat-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
.stat-icon {
  width: 40px; height: 40px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.stat-primary .stat-icon { background: rgba(59,130,246,0.1); color: #3b82f6; }
.stat-success .stat-icon { background: rgba(34,197,94,0.1); color: #22c55e; }
.stat-warning .stat-icon { background: rgba(245,158,11,0.1); color: #f59e0b; }
.stat-cyan .stat-icon { background: rgba(6,182,212,0.1); color: #06b6d4; }
.stat-purple .stat-icon { background: rgba(168,85,247,0.1); color: #a855f7; }
.stat-rose .stat-icon { background: rgba(244,63,94,0.1); color: #f43f5e; }
.stat-text { min-width: 0; }
.stat-value { display: block; font-size: 18px; font-weight: 700; color: #111827; line-height: 1.2; }
.stat-label { display: block; font-size: 11px; color: #6b7280; }
.stat-trend {
  position: absolute; top: 10px; right: 12px;
  display: flex; align-items: center; gap: 2px;
  font-size: 11px; font-weight: 600;
}
.trend-up { color: #22c55e; }
.trend-down { color: #ef4444; }

/* Cards */
.card {
  border: none; border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06); background: white;
}
.card-header {
  background: transparent; border-bottom: 1px solid #f3f4f6; padding: 12px 14px;
}
.card-title { font-size: 13px; font-weight: 600; color: #374151; }
.card-body { padding: 14px; }
.btn-link-sm {
  font-size: 12px; color: #3b82f6; text-decoration: none; font-weight: 500;
}
.btn-link-sm:hover { color: #2563eb; }

/* Chart */
.chart-container { height: 180px; }
.mini-chart-bars {
  display: flex; align-items: flex-end; gap: 4px; height: 100%;
  padding-bottom: 24px; position: relative;
}
.chart-bar-wrapper {
  flex: 1; display: flex; flex-direction: column; align-items: center;
  height: 100%; justify-content: flex-end;
}
.chart-bar {
  width: 100%; max-width: 28px; background: linear-gradient(180deg, #3b82f6, #60a5fa);
  border-radius: 4px 4px 0 0; min-height: 4px; position: relative;
  transition: height 0.5s ease;
}
.chart-bar:hover { opacity: 0.85; }
.bar-tooltip {
  display: none; position: absolute; top: -24px; left: 50%; transform: translateX(-50%);
  background: #1f2937; color: white; padding: 2px 6px; border-radius: 4px;
  font-size: 10px; white-space: nowrap;
}
.chart-bar:hover .bar-tooltip { display: block; }
.bar-label { font-size: 9px; color: #9ca3af; margin-top: 4px; }
.chart-period-btns { display: flex; gap: 4px; }
.period-btn {
  padding: 3px 10px; border: 1px solid #e5e7eb; border-radius: 6px;
  font-size: 11px; background: white; color: #6b7280; cursor: pointer;
  transition: all 0.15s;
}
.period-btn.active { background: #003049; color: white; border-color: #003049; }

/* Breakdown */
.breakdown-list { display: flex; flex-direction: column; gap: 10px; }
.breakdown-item { margin-bottom: 0; }
.breakdown-label { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #374151; margin-bottom: 2px; }
.breakdown-dot { width: 8px; height: 8px; border-radius: 50%; }
.dot-green { background: #22c55e; }
.dot-yellow { background: #f59e0b; }
.dot-blue { background: #3b82f6; }
.dot-gray { background: #9ca3af; }
.breakdown-value { font-size: 16px; font-weight: 700; color: #111827; margin-bottom: 2px; }
.breakdown-bar { height: 4px; background: #f3f4f6; border-radius: 2px; overflow: hidden; }
.bar-fill { height: 100%; border-radius: 2px; transition: width 0.5s ease; }
.bar-green { background: #22c55e; }
.bar-yellow { background: #f59e0b; }
.bar-blue { background: #3b82f6; }
.bar-gray { background: #9ca3af; }

/* Content Grid */
.content-grid {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px;
}
.content-stat {
  display: flex; align-items: center; gap: 8px;
  padding: 10px; background: #f9fafb; border-radius: 8px;
}
.content-stat-value { display: block; font-size: 16px; font-weight: 700; color: #111827; }
.content-stat-label { display: block; font-size: 10px; color: #6b7280; }
.text-primary { color: #3b82f6; }
.text-success { color: #22c55e; }
.text-warning { color: #f59e0b; }
.text-info { color: #06b6d4; }
.text-danger { color: #ef4444; }

/* Status List */
.status-list { display: flex; flex-direction: column; gap: 6px; }
.status-item {
  display: flex; align-items: center; gap: 8px;
  padding: 6px 0; border-bottom: 1px solid #f3f4f6;
}
.status-item:last-child { border-bottom: none; }
.status-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.dot-success { background: #22c55e; }
.dot-warning { background: #f59e0b; }
.dot-danger { background: #ef4444; }
.dot-info { background: #3b82f6; }
.status-label { flex: 1; font-size: 13px; color: #374151; }
.status-count { font-size: 14px; font-weight: 600; color: #111827; }

/* Table */
.table th {
  font-size: 11px; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.5px; color: #6b7280; border-bottom-width: 1px; padding: 8px 14px;
}
.table td { padding: 10px 14px; font-size: 13px; vertical-align: middle; }
.status-badge {
  display: inline-block; padding: 2px 8px; border-radius: 6px;
  font-size: 11px; font-weight: 600; text-transform: capitalize;
}
.status-active { background: rgba(34,197,94,0.1); color: #22c55e; }
.status-draft { background: rgba(107,114,128,0.1); color: #6b7280; }
.status-scheduled { background: rgba(245,158,11,0.1); color: #f59e0b; }
.status-completed { background: rgba(59,130,246,0.1); color: #3b82f6; }
.status-archived { background: rgba(107,114,128,0.1); color: #9ca3af; }
.type-badge {
  display: inline-block; padding: 2px 8px; border-radius: 6px;
  font-size: 11px; font-weight: 500; text-transform: capitalize;
}
.type-question { background: rgba(168,85,247,0.1); color: #a855f7; }
.type-note { background: rgba(34,197,94,0.1); color: #22c55e; }
.type-exam { background: rgba(59,130,246,0.1); color: #3b82f6; }
.type-flashcard { background: rgba(245,158,11,0.1); color: #f59e0b; }

/* Quick Actions */
.quick-actions-grid {
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px;
}
.quick-action-item {
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  padding: 10px 6px; border-radius: 10px; background: #f9fafb;
  text-decoration: none; color: #374151; font-size: 11px; font-weight: 500;
  transition: all 0.2s; text-align: center;
}
.quick-action-item:hover { background: #f0f4ff; color: #3b82f6; transform: translateY(-1px); }

/* Notifications */
.notification-list { max-height: 240px; overflow-y: auto; }
.notification-item {
  display: flex; align-items: flex-start; gap: 8px;
  padding: 8px 14px; border-bottom: 1px solid #f3f4f6;
  transition: background 0.15s;
}
.notification-item:hover { background: #f9fafb; }
.notification-item:last-child { border-bottom: none; }
.notification-icon {
  width: 28px; height: 28px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.notif-info { background: rgba(59,130,246,0.1); color: #3b82f6; }
.notif-success { background: rgba(34,197,94,0.1); color: #22c55e; }
.notif-warning { background: rgba(245,158,11,0.1); color: #f59e0b; }
.notif-error { background: rgba(239,68,68,0.1); color: #ef4444; }
.notif-exam { background: rgba(168,85,247,0.1); color: #a855f7; }
.notif-user { background: rgba(6,182,212,0.1); color: #06b6d4; }
.notif-approval { background: rgba(34,197,94,0.1); color: #22c55e; }
.notification-text { display: block; font-size: 13px; color: #374151; line-height: 1.4; }
.notification-time { display: block; font-size: 11px; color: #9ca3af; margin-top: 2px; }

/* Activity List */
.activity-list { max-height: 280px; overflow-y: auto; }
.activity-item {
  display: flex; align-items: flex-start; gap: 8px;
  padding: 8px 14px; border-bottom: 1px solid #f3f4f6;
}
.activity-item:last-child { border-bottom: none; }
.activity-avatar {
  width: 32px; height: 32px; border-radius: 50%;
  background: linear-gradient(135deg, #fcbf49, #f77f00);
  color: #003049; display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 600; flex-shrink: 0;
}
.activity-text { display: block; font-size: 13px; color: #374151; line-height: 1.4; }
.activity-text strong { color: #111827; }
.activity-time { display: block; font-size: 11px; color: #9ca3af; margin-top: 2px; }

/* Performance */
.performance-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
.perf-item { text-align: center; }
.perf-value { display: block; font-size: 20px; font-weight: 700; color: #111827; }
.perf-label { display: block; font-size: 10px; color: #6b7280; margin-bottom: 4px; }
.perf-bar { height: 4px; background: #f3f4f6; border-radius: 2px; overflow: hidden; }
.perf-fill { height: 100%; background: #f59e0b; border-radius: 2px; transition: width 0.5s; }
.perf-green { background: #22c55e; }
.perf-blue { background: #3b82f6; }
.top-test-item {
  display: flex; justify-content: space-between; align-items: center;
  padding: 6px 0; border-bottom: 1px solid #f3f4f6; font-size: 12px;
}
.top-test-item:last-child { border-bottom: none; }
.top-test-name { color: #374151; }
.top-test-score { font-weight: 600; color: #22c55e; }

/* Info List */
.info-list { list-style: none; margin: 0; padding: 0; }
.info-list li {
  display: flex; justify-content: space-between; align-items: center;
  padding: 6px 0; border-bottom: 1px solid #f3f4f6;
}
.info-list li:last-child { border-bottom: none; }
.info-label { font-size: 12px; color: #6b7280; }
.info-value { font-size: 13px; font-weight: 500; color: #111827; }

/* Admin Users List */
.admin-users-list { max-height: 240px; overflow-y: auto; }
.admin-user-item {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 14px; border-bottom: 1px solid #f3f4f6;
}
.admin-user-item:last-child { border-bottom: none; }
.admin-avatar {
  width: 32px; height: 32px; border-radius: 50%;
  background: linear-gradient(135deg, #003049, #00506e);
  color: white; display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 600; flex-shrink: 0;
}
.admin-info { flex: 1; min-width: 0; }
.admin-name { display: block; font-size: 13px; font-weight: 500; color: #111827; }
.admin-role { display: block; font-size: 11px; color: #6b7280; text-transform: capitalize; }
.admin-status {
  font-size: 11px; font-weight: 500; padding: 2px 8px; border-radius: 10px;
}
.admin-status.active { background: rgba(34,197,94,0.1); color: #22c55e; }
.admin-status.inactive { background: rgba(239,68,68,0.1); color: #ef4444; }

/* Resource Bars */
.resource-bar { height: 6px; background: #f3f4f6; border-radius: 3px; overflow: hidden; }
.resource-fill { height: 100%; border-radius: 3px; transition: width 0.5s; }
.fill-primary { background: #3b82f6; }
.fill-danger { background: #ef4444; }
.fill-blue { background: #06b6d4; }

/* Review Stats */
.review-stats { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
.review-stat-item { text-align: center; padding: 10px; background: #f9fafb; border-radius: 8px; }
.review-stat-value { display: block; font-size: 22px; font-weight: 700; }
.review-stat-label { display: block; font-size: 10px; color: #6b7280; margin-top: 2px; }

/* Approval Summary */
.approval-summary { display: flex; flex-direction: column; gap: 8px; }
.approval-stat { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #374151; }

/* Loading */
.dashboard-loading {
  position: fixed; inset: 0; background: rgba(255,255,255,0.8);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 12px; z-index: 100; font-size: 14px; color: #6b7280;
}
.spinner {
  width: 32px; height: 32px; border: 3px solid #e5e7eb;
  border-top-color: #3b82f6; border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* Responsive */
@media (max-width: 1200px) {
  .stats-6 { grid-template-columns: repeat(3, 1fr); }
  .content-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 768px) {
  .welcome-banner { flex-direction: column; gap: 12px; text-align: center; }
  .welcome-actions { justify-content: center; }
  .stats-grid { grid-template-columns: repeat(2, 1fr); }
  .stats-6 { grid-template-columns: repeat(2, 1fr); }
  .content-grid { grid-template-columns: repeat(2, 1fr); }
  .system-health-bar { flex-direction: column; gap: 8px; }
}
@media (max-width: 480px) {
  .stats-grid { grid-template-columns: 1fr; }
  .content-grid { grid-template-columns: 1fr; }
  .quick-actions-grid { grid-template-columns: repeat(2, 1fr); }
}

/* Dark Mode */
:global(body.dark-mode) .welcome-banner { background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%); }
:global(body.dark-mode) .system-health-bar { background: #1f2937; }
:global(body.dark-mode) .health-item { color: #9ca3af; }
:global(body.dark-mode) .stat-card { background: #1f2937; }
:global(body.dark-mode) .stat-value { color: #f3f4f6; }
:global(body.dark-mode) .stat-label { color: #9ca3af; }
:global(body.dark-mode) .card { background: #1f2937; }
:global(body.dark-mode) .card-header { border-color: #374151; }
:global(body.dark-mode) .card-title { color: #f3f4f6; }
:global(body.dark-mode) .content-stat { background: #111827; }
:global(body.dark-mode) .content-stat-value { color: #f3f4f6; }
:global(body.dark-mode) .content-stat-label { color: #9ca3af; }
:global(body.dark-mode) .quick-action-item { background: #111827; color: #e5e7eb; }
:global(body.dark-mode) .quick-action-item:hover { background: #1e3a5f; }
:global(body.dark-mode) .notification-item { border-color: #374151; }
:global(body.dark-mode) .notification-item:hover { background: #111827; }
:global(body.dark-mode) .notification-text { color: #e5e7eb; }
:global(body.dark-mode) .notification-time { color: #6b7280; }
:global(body.dark-mode) .activity-item { border-color: #374151; }
:global(body.dark-mode) .activity-text { color: #e5e7eb; }
:global(body.dark-mode) .activity-text strong { color: #f3f4f6; }
:global(body.dark-mode) .activity-time { color: #6b7280; }
:global(body.dark-mode) .info-list li { border-color: #374151; }
:global(body.dark-mode) .info-label { color: #9ca3af; }
:global(body.dark-mode) .info-value { color: #f3f4f6; }
:global(body.dark-mode) .admin-user-item { border-color: #374151; }
:global(body.dark-mode) .admin-name { color: #f3f4f6; }
:global(body.dark-mode) .admin-role { color: #9ca3af; }
:global(body.dark-mode) .perf-value { color: #f3f4f6; }
:global(body.dark-mode) .perf-label { color: #9ca3af; }
:global(body.dark-mode) .perf-bar { background: #374151; }
:global(body.dark-mode) .breakdown-value { color: #f3f4f6; }
:global(body.dark-mode) .breakdown-label { color: #d1d5db; }
:global(body.dark-mode) .breakdown-bar { background: #374151; }
:global(body.dark-mode) .review-stat-item { background: #111827; }
:global(body.dark-mode) .review-stat-label { color: #9ca3af; }
:global(body.dark-mode) .approval-stat { color: #d1d5db; }
:global(body.dark-mode) .status-item { border-color: #374151; }
:global(body.dark-mode) .status-label { color: #d1d5db; }
:global(body.dark-mode) .status-count { color: #f3f4f6; }
:global(body.dark-mode) .top-test-item { border-color: #374151; }
:global(body.dark-mode) .top-test-name { color: #d1d5db; }
:global(body.dark-mode) .resource-bar { background: #374151; }
:global(body.dark-mode) .bar-label { color: #6b7280; }
:global(body.dark-mode) .period-btn { background: #1f2937; border-color: #374151; color: #9ca3af; }
:global(body.dark-mode) .period-btn.active { background: #3b82f6; border-color: #3b82f6; color: white; }
:global(body.dark-mode) .table th { background: #111827; color: #9ca3af; border-color: #374151; }
:global(body.dark-mode) .table td { color: #e5e7eb; border-color: #374151; }
:global(body.dark-mode) .table tbody tr:hover { background: #111827; }
:global(body.dark-mode) .btn-link-sm { color: #60a5fa; }
:global(body.dark-mode) .btn-link-sm:hover { color: #93c5fd; }
:global(body.dark-mode) .dashboard-loading { background: rgba(17,24,39,0.8); color: #9ca3af; }
</style>
